/* eslint-disable max-lines */
import cn from 'classnames';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import Input from '@components/atoms/ui-kit/input.tsx';
import React, { useEffect, useState } from 'react';
import { TeamPermission } from '@components/workspace/create-folder.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Drawer from '@components/atoms/modals/drawer.tsx';
import toast from 'react-hot-toast';
import {
  UpdateFolderNameMutationParams,
  useUpdateFolder
} from '@utils/api/folders/use-update-folder.tsx';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';
import { useGetGroups } from '@utils/api/groups/use-get-groups.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { AxiosError } from 'axios';
import { Eye, Lock, Pencil, Plus, X } from 'lucide-react';
import { PermissionType } from '@utils/types/folders/folder';
import { AccessSelector } from '@components/workspace/access-selector.tsx';

type Props = {
  selectedItem: WorkspaceRow;
  teamPermissions: TeamPermission[];
  setTeamPermissions: React.Dispatch<React.SetStateAction<TeamPermission[]>>;
  isEditModalOpen: boolean;
  setIsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<WorkspaceRow | undefined>
  >;
};

export const EditFolderForm = ({
  selectedItem,
  setSelectedItem,
  teamPermissions,
  setTeamPermissions,
  isEditModalOpen,
  setIsEditModalOpen
}: Props) => {
  const { t } = useTranslation();
  const { getColour, getBrandTint, getContrastTextColor } = useThemeContext();
  const { data: groups } = useGetGroups({
    enabled: isEditModalOpen,
    onError: (error: AxiosError) => {
      if (error?.response?.status === 403) return;
    }
  });
  const [newName, setNewName] = useState<string>('');
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [addHovered, setAddHovered] = useState(false);
  const { mutate: updateFolder, isLoading: isSubmitting } = useUpdateFolder();

  useEffect(() => {
    if (selectedItem && isEditModalOpen) {
      setNewName(selectedItem.name || '');

      const existingPermissions = selectedItem.permissions || [];
      const defaultPerm = existingPermissions.find(p => p.group_id === null);
      const teamPerms = existingPermissions.filter(p => p.group_id !== null);

      const mappedPermissions: TeamPermission[] = [
        {
          groupId: '',
          permission: defaultPerm?.permission ?? PermissionType.NO_ACCESS
        },
        ...teamPerms.map(p => ({
          groupId: p.group_id?.toString() || '',
          permission: p.permission
        }))
      ];
      setTeamPermissions(mappedPermissions);
    }
  }, [selectedItem, isEditModalOpen, setTeamPermissions]);

  const activePillStyle = {
    backgroundColor: getColour('activeText'),
    borderColor: getColour('activeText'),
    color: getContrastTextColor(getColour('activeText'))
  };
  const inactivePillStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('hairline')
  };

  const permissionsList = [
    {
      id: PermissionType.NO_ACCESS,
      icon: <Lock className="h-3.5 w-3.5" />,
      description: t('no-access')
    },
    {
      id: PermissionType.READ,
      icon: <Eye className="h-3.5 w-3.5" />,
      description: t('read')
    },
    {
      id: PermissionType.WRITE,
      icon: <Pencil className="h-3.5 w-3.5" />,
      description: t('write')
    }
  ];

  const defaultAccess =
    teamPermissions[0]?.permission ?? PermissionType.NO_ACCESS;
  const isPrivate = defaultAccess === PermissionType.NO_ACCESS;

  const handlePermissionChange = (index: number, permissionId: number) => {
    setTeamPermissions(prev =>
      prev.map((tp, i) =>
        i === index ? { ...tp, permission: permissionId } : tp
      )
    );
  };

  const handleGroupChange = (
    index: number,
    selectedOption: SingleValue<{ label: string; value: string }>
  ) => {
    if (selectedOption) {
      setTeamPermissions(prev =>
        prev.map((tp, i) =>
          i === index ? { ...tp, groupId: selectedOption.value } : tp
        )
      );
    }
  };

  const handleAddTeam = () => {
    setTeamPermissions(prev => [
      ...prev,
      { groupId: '', permission: PermissionType.WRITE }
    ]);
  };

  const handleRemoveTeam = (index: number) => {
    setTeamPermissions(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateFolder = () => {
    if (!selectedItem) {
      toast.error(t('no-folder-selected'));
      return;
    }

    let permissionsPayload;

    if (defaultAccess === PermissionType.NO_ACCESS) {
      const teamRows = teamPermissions.slice(1);
      if (teamRows.some(tp => !tp.groupId.trim())) {
        toast.error(t('please-select-a-group-for-all-teams'));
        return;
      }
      permissionsPayload = teamRows.map(tp => ({
        group_id: parseInt(tp.groupId, 10),
        permission: tp.permission
      }));
    } else {
      permissionsPayload = [{ group_id: null, permission: defaultAccess }];
    }

    const payload: UpdateFolderNameMutationParams = {
      name: newName || selectedItem.name || 'Copy of doc.',
      folder_id: Number(selectedItem.id),
      permissions: permissionsPayload,
      parent_id: selectedItem.parent_id || null
    };

    updateFolder(payload, {
      onSuccess: () => {
        toast.success(t('folder-updated-successfully'));
        setIsEditModalOpen(false);
        setSelectedItem(undefined);
      }
      // Error toast is shown by the global mutation handler
      // (queryClient defaultOptions.mutations.onError) to avoid duplicates.
    });
  };

  const FORM_ID = 'edit-folder-form';

  return (
    <Drawer
      isOpen={isEditModalOpen}
      onClose={() => {
        setIsEditModalOpen(false);
        setSelectedItem(undefined);
      }}
      size="md"
      title={t('edit-folder')}
      caption={t('edit-folder-details')}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          label={t('cancel')}
          size="sm"
          variant="ghost"
          disabled={isSubmitting}
        />,
        <Button
          key="update"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('updating') : t('update')}
          size="sm"
          variant="primary"
          loading={isSubmitting}
        />
      ]}
    >
      <form
        id={FORM_ID}
        className="grid gap-5 py-2"
        onSubmit={e => {
          e.preventDefault();
          handleUpdateFolder();
        }}
      >
        {/* Folder name */}
        <div>
          <label
            htmlFor="name"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('name')}
          </label>
          <Input
            id="name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            type="text"
            autoComplete="off"
            className="w-full"
          />
        </div>

        {/* Access — shared / private cards */}
        <div>
          <p
            className="mb-2 text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('access')}
          </p>
          <AccessSelector
            isPrivate={isPrivate}
            onSelectShared={() =>
              handlePermissionChange(0, PermissionType.WRITE)
            }
            onSelectPrivate={() =>
              handlePermissionChange(0, PermissionType.NO_ACCESS)
            }
            disabled={isSubmitting}
          />
        </div>

        {/* Team permission rows — only visible in private mode */}
        {isPrivate && (
          <div>
            <p
              className="mb-3 text-xs font-semibold uppercase tracking-wide"
              style={{ color: getColour('inactiveText') }}
            >
              {t('team-permissions')}
            </p>

            <div className="space-y-3">
              {teamPermissions.slice(1).map((teamPermission, index) => {
                const actualIndex = index + 1;
                return (
                  <div
                    key={actualIndex}
                    className="relative rounded-xl border border-hairline bg-app-soft p-4"
                  >
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveTeam(actualIndex)}
                      onMouseEnter={() => setHoveredRow(actualIndex)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
                      style={{
                        color:
                          hoveredRow === actualIndex
                            ? 'rgb(239, 68, 68)'
                            : getColour('inactiveText'),
                        backgroundColor:
                          hoveredRow === actualIndex
                            ? 'rgba(239, 68, 68, 0.1)'
                            : 'transparent'
                      }}
                    >
                      <X size={13} />
                    </button>

                    {/* Group select */}
                    <div className="pr-8">
                      <label
                        htmlFor={`group-${actualIndex}`}
                        className="mb-1.5 block text-xs font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('team')}
                      </label>
                      <SelectInput
                        id={`group-${actualIndex}`}
                        className="w-full text-sm"
                        options={
                          groups?.result?.map(group => ({
                            label: group.name,
                            value: group.id.toString()
                          })) || []
                        }
                        onChange={option =>
                          handleGroupChange(actualIndex, option)
                        }
                        value={
                          groups?.result?.find(
                            group =>
                              group.id.toString() === teamPermission.groupId
                          )
                            ? {
                                label:
                                  groups?.result.find(
                                    group =>
                                      group.id.toString() ===
                                      teamPermission.groupId
                                  )?.name ?? '',
                                value: teamPermission.groupId
                              }
                            : null
                        }
                        isMulti={false}
                      />
                    </div>

                    {/* Permission pills */}
                    <div className="mt-3">
                      <div
                        className="mb-1.5 text-xs font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('team-permissions')}
                      </div>
                      <div className="flex gap-2">
                        {permissionsList.map(p => (
                          <label
                            key={p.id}
                            className={cn(
                              'group relative flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full border py-2 text-xs font-semibold transition-colors',
                              'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary'
                            )}
                            style={
                              teamPermission.permission === p.id
                                ? activePillStyle
                                : inactivePillStyle
                            }
                          >
                            <input
                              type="radio"
                              name={`permission-${actualIndex}`}
                              value={p.id}
                              checked={teamPermission.permission === p.id}
                              onChange={() =>
                                handlePermissionChange(actualIndex, p.id)
                              }
                              className="absolute inset-0 appearance-none cursor-pointer focus:outline-none"
                            />
                            <span className="shrink-0">{p.icon}</span>
                            <span>{p.description}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add team — dashed full-width trigger */}
            <button
              type="button"
              onClick={handleAddTeam}
              onMouseEnter={() => setAddHovered(true)}
              onMouseLeave={() => setAddHovered(false)}
              disabled={isSubmitting}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-all duration-200 disabled:opacity-50"
              style={{
                borderColor: addHovered
                  ? getColour('activeText')
                  : getColour('inactiveText'),
                color: addHovered
                  ? getColour('activeText')
                  : getColour('inactiveText'),
                backgroundColor: addHovered ? getBrandTint(0.04) : 'transparent'
              }}
            >
              <Plus size={15} />
              {t('add-team')}
            </button>
          </div>
        )}
      </form>
    </Drawer>
  );
};
